import { formatMoney, type Cents } from '../money.js';
import type { Item, ItemId } from '../types.js';
import type { Settlement } from '../split/settle.js';

/** Amount with no currency marker, for lines under a header that carries it. */
const bareAmount = (amount: Cents): string => (amount / 100).toFixed(2);

export interface ShareOptions {
  /** Download link appended at the bottom. Omit for a clean copy-paste. */
  appLink?: string;
  /** List each person's items underneath their total. */
  includeBreakdown?: boolean;
  /** Needed only when `includeBreakdown` is set. */
  items?: Item[];
}

/**
 * Render a settlement as a message for WhatsApp / Messenger / SMS / mail.
 *
 * Deliberately plain text: no markdown tables (chat apps render them as
 * literal pipes) and no space-padded column alignment (the proportional fonts
 * these apps use destroy it). An em dash between name and amount survives
 * every client.
 */
export function formatShareMessage(
  settlement: Settlement,
  options: ShareOptions = {},
): string {
  const { appLink, includeBreakdown = false, items = [] } = options;
  const { currency } = settlement;
  const itemsById = new Map<ItemId, Item>(items.map((item) => [item.id, item]));

  const header = settlement.merchant
    ? `🧾 ${settlement.merchant} — ${formatMoney(settlement.grandTotal, currency)}`
    : `🧾 Total ${formatMoney(settlement.grandTotal, currency)}`;

  const blocks = settlement.lines.map((line) => {
    // Bare amounts under the header: the currency is already established there,
    // and repeating it on every line turns a scannable list into noise.
    const headline = `${line.name} — ${bareAmount(line.total)}`;
    if (!includeBreakdown) return headline;

    const names = line.items
      .map((id) => itemsById.get(id)?.name)
      .filter((name): name is string => Boolean(name));
    // De-duplicate while preserving order: a person can hold two shares of the
    // same item id only through repeated ops, and repeating the name reads oddly.
    const unique = [...new Set(names)];
    return unique.length > 0 ? `${headline}\n  ${unique.join(', ')}` : headline;
  });

  const sections = [header, blocks.join('\n')];
  if (appLink) sections.push(`Split with AI Bill Splitter → ${appLink}`);
  return sections.join('\n\n');
}
